import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Minus, Search, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtDateTime } from "../../lib/ist";
import ModalPortal from "../../components/ModalPortal";

export default function AdminCredits() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null); // {row, mode: "add"|"deduct"}

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/credits/admin/wallets"); setRows(data); }
    catch { toast.error("Failed to load wallets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r =>
      (r.email || "").toLowerCase().includes(term) ||
      (r.name || "").toLowerCase().includes(term),
    );
  }, [rows, q]);

  return (
    <div className="space-y-4" data-testid="admin-credits-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Wallet size={20} className="text-blue-400" /> AI Credit Wallets
          </h2>
          <p className="text-slate-400 text-sm">Add, deduct or audit a customer&apos;s AI credit balance.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="input pl-8 w-72"
            data-testid="wallet-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-400" /></div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr>
                <th className="py-3 px-4 text-left">Customer</th>
                <th className="text-right">Balance</th>
                <th className="text-right">Purchased</th>
                <th className="text-right">Used</th>
                <th className="text-left pl-6">Last update</th>
                <th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.user_id} className="border-b border-slate-800" data-testid={`wallet-row-${r.user_id}`}>
                  <td className="py-2.5 px-4">
                    <div className="text-white font-medium">{r.name}</div>
                    <div className="text-slate-500 text-xs">{r.email}</div>
                  </td>
                  <td className="text-right text-white font-mono font-bold" data-testid={`wallet-balance-${r.user_id}`}>{r.balance}</td>
                  <td className="text-right text-slate-400 font-mono">{r.total_purchased}</td>
                  <td className="text-right text-slate-400 font-mono">{r.total_used}</td>
                  <td className="pl-6 text-slate-400 text-xs">{r.updated_at ? fmtDateTime(r.updated_at) : "—"}</td>
                  <td className="text-right pr-4">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setPicked({ row: r, mode: "add" })} className="text-emerald-300 hover:text-emerald-200 p-1.5" title="Add credits" data-testid={`add-${r.user_id}`}><Plus size={16} /></button>
                      <button onClick={() => setPicked({ row: r, mode: "deduct" })} className="text-rose-300 hover:text-rose-200 p-1.5" title="Deduct credits" data-testid={`deduct-${r.user_id}`}><Minus size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-slate-500">No customers match this filter.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {picked && <AdjustModal {...picked} onClose={() => setPicked(null)} onSaved={() => { setPicked(null); load(); }} />}
    </div>
  );
}

function AdjustModal({ row, mode, onClose, onSaved }) {
  const [amount, setAmount] = useState(10);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const verb = mode === "add" ? "Add" : "Deduct";

  const save = async () => {
    if (!reason.trim()) { toast.error("Please add a reason for audit trail"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/credits/admin/wallets/${row.user_id}/${mode}`, {
        amount: Number(amount), reason: reason.trim(),
      });
      toast.success(`${verb}ed ${amount} credits. New balance: ${data.balance}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <ModalPortal><div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="adjust-modal">
      <div className="card max-w-md w-full p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-white" data-testid="adjust-close"><X size={18} /></button>
        <h3 className="text-white font-semibold text-lg" style={{ fontFamily: "Outfit" }}>{verb} credits</h3>
        <p className="text-slate-400 text-xs mt-1">Customer: <span className="text-cyan-300">{row.email}</span> · Current balance: <span className="text-white font-mono">{row.balance}</span></p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="label">Amount</label>
            <input type="number" min="1" className="input" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="adjust-amount" />
          </div>
          <div>
            <label className="label">Reason (audit trail)</label>
            <textarea rows="3" className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Promo bonus / Refund correction / Manual top-up after support call" data-testid="adjust-reason" />
          </div>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost !py-2 !px-4" data-testid="adjust-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className={`btn-primary inline-flex items-center gap-2 ${mode === "deduct" ? "!bg-rose-600 hover:!bg-rose-500" : ""}`} data-testid="adjust-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : (mode === "add" ? <Plus size={14} /> : <Minus size={14} />)} {verb}
          </button>
        </div>
      </div>
    </div></ModalPortal>
  );
}
